import * as s from "../StatsStyled";
import { formatBytesAndUnits, formatNumber } from "utils/functions";
import { Text } from "components/atoms/text/Text";
import { formatPercent } from "utils/StatsUtil";
import { ChartTooltipProps } from "api/interfaces/chartInterface.interface";
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

export const ChartTooltip = ({
  sum,
  id,
  value,
  indexValue,
  color,
  unit = "number",
  children,
}: ChartTooltipProps) => {
  const formattedValue = useMemo(() => {
    return unit === "percent"
      ? formatPercent(value, sum)
      : unit === "bytes"
      ? `${formatBytesAndUnits(value).value} ${formatBytesAndUnits(value).unit}`
      : formatNumber(value);
  }, [value, sum, unit]);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;

    const wrapper = anchor.parentElement;
    if (wrapper) {
      wrapper.style.opacity = "0";
    }

    let rafId = 0;
    const updatePosition = () => {
      const target = anchor.parentElement;
      if (target) {
        const rect = target.getBoundingClientRect();
        setPosition({ top: rect.top, left: rect.left });
      }
      rafId = requestAnimationFrame(updatePosition);
    };

    rafId = requestAnimationFrame(updatePosition);

    return () => {
      cancelAnimationFrame(rafId);
      if (wrapper) {
        wrapper.style.opacity = "";
      }
    };
  }, []);

  function ChartContent() {
    return (
      <s.Chart.Tooltip>
        <s.Chart.LegendItem className="chart-tooltip-item">
          {indexValue && (id ? id !== indexValue : true) && (
            <Text fontSize={12}>{String(indexValue)}</Text>
          )}
          {id && (
            <s.Chart.LegendItemLabel $color={color}>
              {String(id).toUpperCase()}
            </s.Chart.LegendItemLabel>
          )}
          <s.Chart.LegendItemValue fontSize={16}>
            {formattedValue}
          </s.Chart.LegendItemValue>
        </s.Chart.LegendItem>
        {children?.map((child) => (
          <s.Chart.ChildItem key={child.label}>
            <s.Chart.ChildItemLabel>- {child.label}</s.Chart.ChildItemLabel>
            <s.Chart.ChildItemValue>{child.value}</s.Chart.ChildItemValue>
          </s.Chart.ChildItem>
        ))}
      </s.Chart.Tooltip>
    );
  }
  return (
    <>
      <div ref={anchorRef}>
        <ChartContent />
      </div>
      {position && typeof document !== "undefined"
        ? createPortal(
            <div
              style={{
                position: "fixed",
                top: position.top,
                left: position.left,
                zIndex: 100,
                pointerEvents: "none",
              }}
            >
              <ChartContent />
            </div>,
            document.body
          )
        : null}
    </>
  );
};
